import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fileApi } from '../api/endpoints.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import Modal from './Modal.jsx';

const KB = 1024;
const humanSize = (bytes) =>
  bytes >= KB * KB ? `${(bytes / (KB * KB)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / KB))} kB`;

/**
 * What can be shown rather than downloaded.
 *
 * A picture and a PDF are the two things people attach expecting to LOOK at
 * them - having to download, find and open the file to check a screenshot is
 * three steps too many. Plain text and CSV are readable too, so they render as
 * text. Anything else downloads, because pretending to preview a file the
 * browser cannot render is worse than not offering it.
 */
const canPreview = (mimeType = '') =>
  mimeType.startsWith('image/') || mimeType === 'application/pdf' || mimeType.startsWith('text/');

/**
 * Files attached to a comment or a chat message.
 *
 * Chips are buttons, not links, and that is the whole point: an attachment is
 * PRIVATE, so `/api/files/:id` needs a bearer token - and neither <a href> nor
 * <img src> can send one. Rather than putting the token in the URL, where it
 * would end up in logs and referrers, the bytes are fetched with the header and
 * turned into a blob the browser can display locally.
 */
export const AttachmentList = ({ files }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const [preview, setPreview] = useState(null); // { file, url, text }
  const [loadingId, setLoadingId] = useState(null);

  const close = () => {
    // The blob stays in memory until it is revoked, and a customer's contract
    // is not something to leave lying around in the tab.
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const open = async (file) => {
    setLoadingId(file.id);
    try {
      if (!canPreview(file.mimeType)) {
        await fileApi.download(file.id, file.filename);
        return;
      }
      const blob = await fileApi.blob(file.id);
      setPreview({
        file,
        url: URL.createObjectURL(blob),
        text: file.mimeType.startsWith('text/') ? await blob.text() : null,
      });
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setLoadingId(null);
    }
  };

  if (!files?.length) return null;

  return (
    <>
      <div className="chip-row">
        {files.map((file) => (
          <button
            key={file.id}
            type="button"
            className="chip chip--file"
            onClick={() => open(file)}
            disabled={loadingId === file.id}
            title={file.filename}
          >
            <span className="chip__name">{file.filename}</span>
            <span className="chip__size">{humanSize(file.size)}</span>
            <span className="chip__hint">
              {loadingId === file.id
                ? t('common.loading')
                : canPreview(file.mimeType)
                  ? t('files.preview')
                  : t('files.download')}
            </span>
          </button>
        ))}
      </div>

      <Modal
        open={Boolean(preview)}
        title={preview?.file.filename ?? ''}
        onClose={close}
        width={980}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => fileApi.download(preview.file.id, preview.file.filename)}
            >
              {t('files.download')}
            </button>
            <button type="button" className="btn btn--primary" onClick={close}>
              {t('common.close')}
            </button>
          </>
        }
      >
        {preview ? (
          <div className="preview">
            {preview.file.mimeType.startsWith('image/') ? (
              <img className="preview__image" src={preview.url} alt={preview.file.filename} />
            ) : preview.file.mimeType === 'application/pdf' ? (
              <iframe className="preview__frame" src={preview.url} title={preview.file.filename} />
            ) : (
              <pre className="preview__text">{preview.text}</pre>
            )}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

/**
 * The picker that sits next to a message box.
 *
 * Files are uploaded as soon as they are chosen, not on send: the caller then
 * only has ids to pass, and a slow upload does not sit between pressing Send
 * and the message appearing. `value` is the list of uploaded files, held by the
 * caller so it can clear it once the message has gone.
 */
export const AttachmentPicker = ({ value = [], onChange, max = 5, disabled = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (event) => {
    const chosen = [...event.target.files];
    event.target.value = '';
    if (!chosen.length) return;

    if (value.length + chosen.length > max) {
      toast.error(t('files.tooMany', { max }));
      return;
    }

    setBusy(true);
    try {
      const uploaded = [];
      for (const file of chosen) uploaded.push(await fileApi.upload(file));
      onChange([...value, ...uploaded]);
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="attach">
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={pick}
        accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,application/pdf,text/plain,text/csv"
      />
      <button
        type="button"
        className="btn btn--small"
        onClick={() => input.current?.click()}
        disabled={disabled || busy || value.length >= max}
      >
        {busy ? t('common.loading') : t('files.attach')}
      </button>

      {value.length ? (
        <div className="chip-row">
          {value.map((file) => (
            <span key={file.id} className="chip">
              <span className="chip__name">{file.filename}</span>
              <button
                type="button"
                className="chip__remove"
                onClick={() => onChange(value.filter((row) => row.id !== file.id))}
                aria-label={t('common.delete')}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AttachmentPicker;
